/**
 * Masonry Layout Utilities
 * 
 * Column distribution algorithm for true masonry layout.
 * Items are distributed into columns based on cumulative height,
 * always placing the next item in the shortest column.
 */

/**
 * Default responsive breakpoints for column count
 */
export const DEFAULT_BREAKPOINTS = {
    xl: { minWidth: 1300, columns: 5 },
    lg: { minWidth: 1000, columns: 4 },
    md: { minWidth: 700, columns: 3 },
    sm: { minWidth: 350, columns: 2 },
    xs: { minWidth: 0, columns: 1 },
};

/**
 * Get column count based on container width
 * 
 * @param {number} containerWidth - Width of the container in pixels
 * @param {Object} customBreakpoints - Custom breakpoint configuration from database (optional)
 * @param {number} baseColumns - Base column count from database (optional, defaults to breakpoints.xl.columns)
 * @returns {number} Number of columns
 */
export function getColumnCount(containerWidth, customBreakpoints = null, baseColumns = null) {
    // Use custom breakpoints if provided, otherwise use defaults
    const breakpoints = customBreakpoints || DEFAULT_BREAKPOINTS;
    
    // If baseColumns is provided, use it as the maximum and scale down responsively
    if (baseColumns !== null && baseColumns !== undefined) {
        const base = Math.max(2, Math.min(6, baseColumns)); // Clamp between 2-6
        
        // Scale down based on screen width using breakpoints, but never exceed baseColumns
        if (containerWidth > breakpoints.xl.minWidth) return base;
        if (containerWidth > breakpoints.lg.minWidth) return Math.min(base, breakpoints.lg.columns);
        if (containerWidth > breakpoints.md.minWidth) return Math.min(base, breakpoints.md.columns);
        if (containerWidth > breakpoints.sm.minWidth) return Math.min(base, breakpoints.sm.columns);
        return breakpoints.xs.columns; // Always use xs.columns for very small screens
    }
    
    // Default behavior without baseColumns - use breakpoint columns directly
    if (containerWidth > breakpoints.xl.minWidth) return breakpoints.xl.columns;
    if (containerWidth > breakpoints.lg.minWidth) return breakpoints.lg.columns;
    if (containerWidth > breakpoints.md.minWidth) return breakpoints.md.columns;
    if (containerWidth > breakpoints.sm.minWidth) return breakpoints.sm.columns;
    return breakpoints.xs.columns;
}

/**
 * Get estimated height for an image based on its dimensions
 * Uses aspect ratio to calculate relative height (assuming equal column widths)
 * 
 * @param {Object} item - Item with dimensions property
 * @param {number} columnWidth - Width of column (default 1 for relative calculations)
 * @returns {number} Estimated height
 */
export function getImageHeight(item, columnWidth = 1) {
    if (!item?.dimensions?.width || !item?.dimensions?.height) {
        return columnWidth; // Default to square if dimensions unavailable
    }
    const aspectRatio = item.dimensions.height / item.dimensions.width;
    return columnWidth * aspectRatio;
}

/**
 * Find the index of the column with the smallest height
 * 
 * @param {Array<{height: number}>} columns - Array of column objects with height property
 * @returns {number} Index of the shortest column
 */
export function findShortestColumn(columns) {
    return columns.reduce((minIdx, col, idx) => 
        col.height < columns[minIdx].height ? idx : minIdx, 0);
}

/**
 * Distribute items into columns using the canonical masonry algorithm.
 * Each item goes into the column with the smallest cumulative height.
 * 
 * @param {Array} items - Items with height information (dimensions or aspect ratio)
 * @param {number} columnCount - Number of columns
 * @param {Function} getItemHeight - Function to estimate item height from item data
 * @returns {Array<Array<{item: any, originalIndex: number}>>} - Array of columns, each containing items with their original indices
 */
export function distributeItemsToColumns(items, columnCount, getItemHeight = getImageHeight) {
    if (!items || items.length === 0) {
        return Array.from({ length: columnCount }, () => []);
    }

    if (columnCount <= 0) {
        return [items.map((item, originalIndex) => ({ item, originalIndex }))];
    }

    // Initialize columns with height tracking
    const columns = Array.from({ length: columnCount }, () => ({
        items: [],
        height: 0,
    }));

    // Distribute each item to the shortest column
    items.forEach((item, originalIndex) => {
        const shortestIdx = findShortestColumn(columns);
        const itemHeight = getItemHeight(item);
        
        columns[shortestIdx].items.push({ item, originalIndex });
        columns[shortestIdx].height += itemHeight;
    });

    // Return just the items arrays (without height tracking)
    return columns.map(col => col.items);
}

/**
 * Get a flat list of item IDs in their visual order (left-to-right, top-to-bottom)
 * Useful for drag-and-drop reordering
 * 
 * @param {Array<Array<{item: any, originalIndex: number}>>} columns - Distributed columns
 * @returns {Array<number>} Original indices in visual reading order
 */
export function getVisualOrder(columns) {
    const maxLength = Math.max(...columns.map(col => col.length));
    const order = [];
    
    for (let row = 0; row < maxLength; row++) {
        for (let col = 0; col < columns.length; col++) {
            if (columns[col][row]) {
                order.push(columns[col][row].originalIndex);
            }
        }
    }
    
    return order;
}
